import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { ConfigPaths } from "@omni/types/utils/configPaths";
import { Translate } from "@omni/types/utils/translate";
import { get } from "@omni/utils/object.utils";
import { useEffect } from "react";
import { FieldPathValue, Path, useFormContext } from "react-hook-form";

import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";

import { useAddressFormBlockContext } from "../addressFormBlock.context";
import { PaymentAddressFormValues } from "../partials/paymentAddress.schema";

const usePaymentHook = () => {
    const { getValues, setValue, setError } =
        useFormContext<PaymentAddressFormValues>();
    const { displayAddressFields, validate, isValidating } =
        useAddressFormBlockContext();
    const { errorFields } = useAddressValidationContext();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (errorFields.length > 0) {
            const values = getValues();

            const fieldErrors = errorFields.reduce((acc, curr) => {
                const key =
                    `payment.${curr.key}` as ConfigPaths<PaymentAddressFormValues>;
                if (get(values, key) === curr.value) {
                    acc.set(
                        key as Path<PaymentAddressFormValues>,
                        () => (t: Translate) =>
                            t(`bpost.validation.invalid.field.${curr.key}`)
                    );
                }
                return acc;
            }, new Map<
                Path<PaymentAddressFormValues>,
                () => (t: Translate) => string
            >());

            fieldErrors.forEach((value, key) => {
                setError(key, {
                    type: "validate",
                    message: value
                });
            });
        }
    }, [errorFields]);

    useEffect(() => {
        const { payment: address } = getValues();

        validate(address, (addressLine) => {
            setValue(
                "payment.validationStatus",
                AddressValidationStatus.Validated as FieldPathValue<
                    PaymentAddressFormValues,
                    "payment.validationStatus"
                >
            );
            setValue(
                "payment.address",
                addressLine as FieldPathValue<
                    PaymentAddressFormValues,
                    "payment.address"
                >
            );
        });
    }, [getValues, setValue, validate]);

    useEffect(() => {
        setValue(
            "payment.selected",
            !displayAddressFields as FieldPathValue<
                PaymentAddressFormValues,
                "payment.selected"
            >
        );
    }, [displayAddressFields, setValue]);

    return {
        loading: isValidating
    };
};

export { usePaymentHook };
